import { useMemo } from 'react';

import { useAssigneeOptionsList } from 'hooks/useAssigneeOptionsList';

import { useProjectTreeStore } from 'store/projectContract.store';

import { useQueryActivityCodesByVersion } from 'api/queries/activityCodesByVersion.query';
import { useQueryClustersList } from 'api/queries/clustersList.query';
import { useQueryGetCardActivityTypes } from 'api/queries/getCardActivityTypes.query';
import { useQueryGetCardAssignees } from 'api/queries/getCardAssignees.query';
import { useQueryGetCardImportance } from 'api/queries/getCardImportance.query';
import { useQueryGetCardResourceTypes } from 'api/queries/getCardResourceType.query';
import { useQueryGetCardTypes } from 'api/queries/getCardTypes.query';
import { useQueryGetCardUrgent } from 'api/queries/getCardUrgent.query';
import { useQueryGetCompletionPlanFragnetMilestones } from 'api/queries/getCompletionPlanFragnetMilestones.query';
import { useQueryGetFragnetCompletion } from 'api/queries/getFragnetCompletion.query';
import { useQueryGetResourceListForChart } from 'api/queries/getResourceListForChart.query';
import { useQueryGetSingleMilestoneFragnet } from 'api/queries/getSingleMilestoneFragnet.query';
import { useQueryGetSingleWbs } from 'api/queries/getWbs.query';

import { FilterNames, getFiltersData } from 'components/common/GlobalFiltersInPage/globalFiltersInPage.utils';

import { generateFilterByType } from 'components/Dashboards/Project/Components/CustomDashboard/PinboardWidgetPills/PinboardWidgetPills.utils';
import { IFilter } from 'components/Dashboards/Project/Components/CustomDashboard/utils/utils.type';

const fragnetMilestoneNames = [FilterNames.FRAGNET_MILESTONE_ID_CARD_BOARD, FilterNames.FRAGNET_SEARCH];
const wbsNames = [FilterNames.WBS_SEARCH];

interface IUseActivityCodesHook {
    filter?: {
        filters: IFilter[];
    };
    latestVersionId?: number;
    source?: unknown;
    widgetId?: string;
}

export const useGlobalFiltersHook = ({
    filter,
    latestVersionId,
    source = null,
    widgetId,
}: IUseActivityCodesHook): { generateFilters: IFilter[]; pillsList: unknown[] } => {
    const {
        activityCodes,
        milestoneFragnet,
        forwardMilestoneFragnet,
        completionPlanMilestoneFragnet,
        wbs,
        resourceOptions,
        assigneeOptionsList,
        clusters,
        completionPlanOptions,
        resourceTypeOptions,
        activityTypesOptions,
        importantOptions,
        urgentOptions,
        cardTypeOptions,
        updateByOptions,
    } = useGetFilterValues({
        latestVersionId,
        filter,
        source,
        isInReportViewPage: Boolean(widgetId),
    });

    // expects list of object with name and data
    // [
    //      { name: 'FILTER_NAME', data: [100,200] },
    //      { name: 'ANOTHER_FILTER_NAME', data: ['my_value'] }
    // ]

    const generateFilters = filter
        ? filter?.filters.map((item) =>
              generateFilterByType({
                  item,
                  activityCodes,
                  milestoneFragnet,
                  forwardMilestoneFragnet,
                  completionPlanMilestoneFragnet,
                  wbs,
                  resourceOptions,
                  assigneeOptionsList,
                  clusters,
                  completionPlanOptions,
                  resourceTypeOptions,
                  activityTypesOptions,
                  importantOptions,
                  urgentOptions,
                  cardTypeOptions,
                  updateByOptions,
              }),
          )
        : [];

    // new & improved mechanism, expect an object with key value structure:
    // {
    //      filterName: [100,200],
    //      anotherFilterName: 'my_value'
    // }

    const pillsList = useMemo(() => {
        if (source) {
            const list = getFiltersData(source);
            return list.map((item) =>
                generateFilterByType({
                    item,
                    activityCodes,
                    milestoneFragnet,
                    forwardMilestoneFragnet,
                    completionPlanMilestoneFragnet,
                    wbs,
                    resourceOptions,
                    assigneeOptionsList,
                    clusters,
                    completionPlanOptions,
                    resourceTypeOptions,
                    activityTypesOptions,
                    importantOptions,
                    urgentOptions,
                    cardTypeOptions,
                    updateByOptions,
                }),
            );
        } else {
            return [];
        }
    }, [
        source,
        activityCodes,
        milestoneFragnet,
        forwardMilestoneFragnet,
        completionPlanMilestoneFragnet,
        wbs,
        resourceOptions,
        assigneeOptionsList,
        clusters,
        completionPlanOptions,
        resourceTypeOptions,
        activityTypesOptions,
        importantOptions,
        urgentOptions,
        cardTypeOptions,
        updateByOptions,
    ]);

    return { generateFilters, pillsList };
};

const useGetFilterValues = ({ latestVersionId, filter, source, isInReportViewPage }) => {
    const { project } = useProjectTreeStore();
    const { options: assigneeOptionsList } = useAssigneeOptionsList();
    const { data: clusters } = useQueryClustersList({
        idList: source?.cluster && isInReportViewPage && latestVersionId ? [latestVersionId] : [],
        projectId: project?.id,
        compare: 'ITSELF',
    });
    const { data: activityCodes } = useQueryActivityCodesByVersion({
        latestVersion: source?.activityCodes && isInReportViewPage ? latestVersionId : undefined,
    });

    const milestoneFragnetId = useMemo(() => {
        if (source) {
            return source.milestoneFragnet;
        }
        if (filter) {
            const milestoneFragnet = filter.filters.find((item) => fragnetMilestoneNames.includes(item.name));
            return milestoneFragnet?.data?.[0];
        }
    }, [filter, source]);

    const forwardMilestoneFragnetId = useMemo(() => {
        if (source) {
            return source.activityForwardFragnet;
        }
        if (filter) {
            const activityForwardFragnet = filter.filters.find((item) => fragnetMilestoneNames.includes(item.name));
            return activityForwardFragnet?.data?.[0];
        }
    }, [filter, source]);

    const completionPlanFragnetMilestonesId = useMemo(() => {
        if (source) {
            return source.completionPlanFragnetMilestones;
        }
        if (filter) {
            const completionPlanFragnetMilestones = filter.filters.find((item) =>
                fragnetMilestoneNames.includes(item.name),
            );
            return completionPlanFragnetMilestones?.data?.[0];
        }
    }, [filter, source]);

    //TODO: make to separate queries for milestoneFragnet and forwardMilestoneFragnet
    const { data: milestoneFragnet } = useQueryGetSingleMilestoneFragnet({
        latestVersion: source?.milestoneFragnet && isInReportViewPage ? latestVersionId : undefined,
        taskVersionHashCode: milestoneFragnetId, // make sure BE can perform search by task_version_hash_code
    });

    const { data: forwardMilestoneFragnet } = useQueryGetSingleMilestoneFragnet({
        latestVersion: source?.activityForwardFragnet && isInReportViewPage ? latestVersionId : undefined,
        taskVersionHashCode: forwardMilestoneFragnetId, // make sure BE can perform search by task_version_hash_code
    });

    const { data: completionPlanMilestoneFragnet } = useQueryGetCompletionPlanFragnetMilestones({
        latestVersion: source?.completionPlanFragnetMilestones && isInReportViewPage ? latestVersionId : undefined,
        taskVersionHashCode: completionPlanFragnetMilestonesId, // make sure BE can perform search by task_version_hash_code
    });

    const wbsId = useMemo(() => {
        if (source) {
            return source.wbs;
        }
        if (filter) {
            const wbs = filter.filters.find((item) => wbsNames.includes(item.name));
            return wbs?.data?.[0];
        }
    }, [filter, source]);

    const { data: wbs } = useQueryGetSingleWbs({
        latestVersion: source?.wbs && isInReportViewPage ? latestVersionId : undefined,
        taskId: wbsId, // make sure BE can perform search by task_version_hash_code
    });

    const { data: resourceOptions } = useQueryGetResourceListForChart({
        latestVersion: source?.resource && isInReportViewPage ? latestVersionId : undefined,
    });

    const { data: completionPlanOptions } = useQueryGetFragnetCompletion({
        metaDataId: source?.completionPlanSegment && isInReportViewPage ? latestVersionId : undefined,
    });

    const { data: resourceTypeOptions } = useQueryGetCardResourceTypes({
        metaDataId: source?.resourceType && isInReportViewPage ? latestVersionId : undefined,
    });

    const { data: activityTypesOptions } = useQueryGetCardActivityTypes({
        metaDataId: source?.cardActivity && isInReportViewPage ? latestVersionId : undefined,
    });

    const { data: importantOptions } = useQueryGetCardImportance({
        metaDataId: source?.important && isInReportViewPage ? latestVersionId : undefined,
    });

    const { data: urgentOptions } = useQueryGetCardUrgent({
        metaDataId: source?.urgent && isInReportViewPage ? latestVersionId : undefined,
    });

    const { data: cardTypeOptions } = useQueryGetCardTypes({
        metaDataId: source?.cardType && isInReportViewPage ? latestVersionId : undefined,
    });

    const { data: updateByOptions } = useQueryGetCardAssignees({
        metaDataId: source?.updatedBy && isInReportViewPage ? latestVersionId : undefined,
    });

    return {
        activityCodes,
        milestoneFragnet,
        forwardMilestoneFragnet,
        completionPlanMilestoneFragnet,
        wbs,
        resourceOptions,
        assigneeOptionsList,
        clusters,
        completionPlanOptions,
        resourceTypeOptions,
        activityTypesOptions,
        importantOptions,
        urgentOptions,
        cardTypeOptions,
        updateByOptions,
    };
};
